import type { MapRef } from 'react-map-gl/mapbox';
import { RiskVolume } from '../types/gcs/foreignAircraft';
import type { GcsData } from '../types/gcs/gcsData';
import type { MissionId } from '../types/gcs/mission';
import type { VehicleId } from '../types/gcs/vehicle';

export const FIT_BOUNDS_DURATION_MS = 250;

interface MapPadding {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

/**
 * Calculates dynamic map padding, accounting for side panels and vehicle panel.
 * Reads from CSS variables: --panel-gap, --side-panel-width, --vehicle-panel-width, --centers-panel-width
 *
 * @param isVehiclePanelOpen - Whether the vehicle panel is currently open
 * @returns Padding object with top, bottom, left, and right values in pixels
 */
export function calculateMapPadding(isVehiclePanelOpen: boolean): MapPadding {
  const root = document.documentElement;
  const styles = getComputedStyle(root);

  const panelGap = parseFloat(styles.getPropertyValue('--panel-gap')) || 16;
  const sidePanelWidth = parseFloat(styles.getPropertyValue('--side-panel-width')) || 420;
  const vehiclePanelWidth = parseFloat(styles.getPropertyValue('--vehicle-panel-width')) || 420;
  const centersPanelWidth = parseFloat(styles.getPropertyValue('--centers-panel-width')) || 360;
  const standardPadding = 48;

  // Base left padding: gap + side panel + gap + extra padding
  let leftPadding = panelGap + sidePanelWidth + panelGap + standardPadding;

  // Add vehicle panel width if it's open
  if (isVehiclePanelOpen) {
    leftPadding += vehiclePanelWidth + panelGap;
  }

  // Right padding: gap + centers panel + gap + extra padding
  const rightPadding = panelGap + centersPanelWidth + panelGap + standardPadding;

  return {
    top: standardPadding,
    bottom: standardPadding,
    left: leftPadding,
    right: rightPadding
  };
}

export function getVehicleFocusCoordinates(gcsData: GcsData, vehicleId: VehicleId): Array<[number, number]> {
  const coordinates: Array<[number, number]> = [];

  const launchpadId = gcsData.vehicleLaunchpadMap.get(vehicleId);
  if (launchpadId) {
    const launchpad = gcsData.launchpads.data?.find((l) => l.id === launchpadId);
    if (launchpad) {
      coordinates.push([launchpad.location.lng, launchpad.location.lat]);
    }
  }

  const liveMission = gcsData.liveMissionsMap.get(vehicleId);
  if (liveMission) {
    const flightData = gcsData.missionFlightDataMap.get(liveMission.missionId);
    if (flightData?.location) {
      coordinates.push([flightData.location.lng, flightData.location.lat]);
    }

    const missionRoute = gcsData.missionRouteMap.get(liveMission.missionId);
    if (missionRoute?.route) {
      missionRoute.route.forEach((node) => {
        coordinates.push([node.location.lng, node.location.lat]);
      });
    }

    coordinates.push([
      liveMission.package.order.deliveryPoint.location.lng,
      liveMission.package.order.deliveryPoint.location.lat
    ]);

    if (liveMission.pickupPoint) {
      coordinates.push([liveMission.pickupPoint.location.lng, liveMission.pickupPoint.location.lat]);
    }
  }

  gcsData.foreignAircraftDetailsMap.forEach((details, aircraftId) => {
    const riskVolume = gcsData.foreignAircraftRiskVolumeMap.get(aircraftId);
    if (riskVolume && riskVolume.riskVolume !== RiskVolume.NOMINAL) {
      coordinates.push([details.location.lng, details.location.lat]);
    }
  });

  return coordinates;
}

export function getCenterFocusCoordinates(
  gcsData: GcsData,
  visibleMissionIds: Set<MissionId>
): Array<[number, number]> {
  const coordinates: Array<[number, number]> = [];

  gcsData.flightNetwork.data?.centerControlZone?.points.forEach((point) => {
    coordinates.push([point.lng, point.lat]);
  });

  gcsData.launchpads.data?.forEach((launchpad) => {
    coordinates.push([launchpad.location.lng, launchpad.location.lat]);
  });

  gcsData.liveMissionsMap.forEach((mission) => {
    if (!visibleMissionIds.has(mission.missionId)) return;

    if (mission.pickupPoint) {
      coordinates.push([mission.pickupPoint.location.lng, mission.pickupPoint.location.lat]);
    }
    coordinates.push([
      mission.package.order.deliveryPoint.location.lng,
      mission.package.order.deliveryPoint.location.lat
    ]);
  });

  gcsData.missionRouteMap.forEach((routeInfo, missionId) => {
    if (!visibleMissionIds.has(missionId)) return;

    routeInfo.route.forEach((node) => {
      coordinates.push([node.location.lng, node.location.lat]);
    });
  });

  gcsData.missionFailureModeRouteInfoMap.forEach((routeInfo, missionId) => {
    if (!visibleMissionIds.has(missionId)) return;

    routeInfo.routeToSafePoint.forEach((point) => {
      coordinates.push([point.lng, point.lat]);
    });
    routeInfo.safePoints.forEach((safePoint) => {
      coordinates.push([safePoint.location.lng, safePoint.location.lat]);
    });
  });

  gcsData.missionFlightDataMap.forEach((flightData) => {
    coordinates.push([flightData.location.lng, flightData.location.lat]);
  });

  gcsData.foreignAircraftDetailsMap.forEach((aircraftDetails) => {
    coordinates.push([aircraftDetails.location.lng, aircraftDetails.location.lat]);
  });

  return coordinates;
}

function clampBounds(
  minLng: number,
  maxLng: number,
  minLat: number,
  maxLat: number
): { minLng: number; maxLng: number; minLat: number; maxLat: number } {
  const minDiff = 0.01;
  const lngDiff = maxLng - minLng;
  if (lngDiff < minDiff) {
    const expansion = (minDiff - lngDiff) / 2;
    minLng -= expansion;
    maxLng += expansion;
  }
  const latDiff = maxLat - minLat;
  if (latDiff < minDiff) {
    const expansion = (minDiff - latDiff) / 2;
    minLat -= expansion;
    maxLat += expansion;
  }
  return { minLng, maxLng, minLat, maxLat };
}

export function fitMapToCoordinates(
  mapRef: MapRef,
  coordinates: Array<[number, number]>,
  isVehiclePanelOpen: boolean
): void {
  const longitudes = coordinates.map(([lng]) => lng);
  const latitudes = coordinates.map(([, lat]) => lat);

  const { minLng, maxLng, minLat, maxLat } = clampBounds(
    Math.min(...longitudes),
    Math.max(...longitudes),
    Math.min(...latitudes),
    Math.max(...latitudes)
  );

  mapRef.fitBounds(
    [
      [minLng, minLat],
      [maxLng, maxLat]
    ],
    {
      padding: calculateMapPadding(isVehiclePanelOpen),
      duration: FIT_BOUNDS_DURATION_MS,
      essential: true
    }
  );
}
